import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';

// ─── CameraCursor ─────────────────────────────────────────────────────────────
// Custom cursor shaped as an SVG camera:
//   • Lens centre tracks the mouse exactly
//   • Links / buttons → camera turns red
//   • data-cursor="view"|"play" → red + context label
//   • mousedown → shutter squish + full-screen white flash
//   • Hidden on touch / coarse-pointer devices (mobile)
// ─────────────────────────────────────────────────────────────────────────────

export default function MagneticCursor() {
  // Detect touch once on mount — stable for the component lifetime
  const [isTouch] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches
  );

  const outerRef = useRef(null);   // translates to cursor position
  const iconRef  = useRef(null);   // scales on click (inner)
  const flashRef = useRef(null);   // full-screen flash overlay
  const lblRef   = useRef(null);   // context label (SHOOT / PLAY)

  useEffect(() => {
    if (isTouch) return;  // no mouse cursor on touch devices
    const outer = outerRef.current;
    const icon  = iconRef.current;
    const flash = flashRef.current;
    const lbl   = lblRef.current;
    if (!outer || !icon || !flash || !lbl) return;

    // Camera SVG is 32×26 rendered; lens sits at (16, 16) in px
    const OFFSET_X = 16;
    const OFFSET_Y = 16;

    const onMove = (e) => {
      const mx = e.clientX;
      const my = e.clientY;

      // Exact position — lens centre locked to cursor
      outer.style.transform = `translate(${mx - OFFSET_X}px, ${my - OFFSET_Y}px)`;
      lbl.style.transform   = `translate(${mx - OFFSET_X - 4}px, ${my + 14}px)`;

      // ── Context detection ───────────────────────────────────────────────
      const el         = document.elementFromPoint(mx, my);
      const cursorAttr = el?.closest('[data-cursor]')?.getAttribute('data-cursor');
      const isLink     = el?.closest('button, a, [role="button"]');

      if (cursorAttr === 'play' || cursorAttr === 'view') {
        icon.style.color  = '#ff2a1f';
        // Only show label for 'view' (SHOOT); 'play' just turns the camera red
        lbl.textContent   = cursorAttr === 'view' ? 'SHOOT' : '';
        lbl.style.opacity = cursorAttr === 'view' ? '1' : '0';
      } else if (isLink) {
        icon.style.color   = '#ff2a1f';
        lbl.textContent    = '';
        lbl.style.opacity  = '0';
      } else {
        icon.style.color   = 'rgba(242,239,232,0.88)';
        lbl.textContent    = '';
        lbl.style.opacity  = '0';
      }
    };

    const onDown = (e) => {
      const mx = e.clientX;
      const my = e.clientY;
      const r  = 38; // half-size of flash circle

      // ── Shutter squish ────────────────────────────────────────────────
      gsap.killTweensOf(icon);
      gsap.timeline()
        .to(icon, { scale: 0.78, duration: 0.06, ease: 'power2.in' })
        .to(icon, { scale: 1.10, duration: 0.14, ease: 'power1.out' })
        .to(icon, { scale: 1.00, duration: 0.22, ease: 'back.out(3)' });

      // ── Radial flash burst at cursor ──────────────────────────────────
      gsap.killTweensOf(flash);
      gsap.timeline()
        .set(flash,  { display: 'block', left: mx - r, top: my - r, scale: 0.2, opacity: 0 })
        .to(flash,   { scale: 1,   opacity: 1, duration: 0.055, ease: 'none' })
        .to(flash,   { scale: 2.2, opacity: 0, duration: 0.32,  ease: 'power2.out' })
        .set(flash,  { display: 'none' });
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('mousedown', onDown);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mousedown', onDown);
    };
  }, []);

  if (isTouch) return null;

  return (
    <>
      {/* ── Camera cursor ──────────────────────────────────────────── */}
      <div
        ref={outerRef}
        aria-hidden="true"
        style={{
          position:      'fixed',
          top:           0,
          left:          0,
          pointerEvents: 'none',
          zIndex:        100003,
          willChange:    'transform',
        }}
      >
        {/* Inner element — GSAP animates scale here without touching outer translate */}
        <div
          ref={iconRef}
          style={{
            color:      'rgba(242,239,232,0.88)',
            filter:     'drop-shadow(0 0 4px rgba(0,0,0,0.55)) drop-shadow(0 1px 2px rgba(0,0,0,0.35))',
            transition: 'color 180ms ease',
            lineHeight: 0,
          }}
        >
          {/* Camera SVG — 32×26px, lens centre at (16,16) */}
          <svg
            width="32"
            height="26"
            viewBox="0 0 32 26"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {/* Body */}
            <rect x="1" y="7" width="30" height="18" rx="2.5" />
            {/* Viewfinder bump */}
            <path d="M11 7V5C11 3.9 11.9 3 13 3h6c1.1 0 2 .9 2 2v2" />
            {/* Lens outer ring */}
            <circle cx="16" cy="16" r="5.5" />
            {/* Lens inner ring (depth) */}
            <circle cx="16" cy="16" r="2.5" />
            {/* Flash unit */}
            <rect x="3" y="9.5" width="4" height="3" rx="1" fill="currentColor" stroke="none" opacity="0.6" />
          </svg>
        </div>
      </div>

      {/* ── Context label ───────────────────────────────────────────── */}
      <div
        ref={lblRef}
        aria-hidden="true"
        style={{
          position:      'fixed',
          top:           0,
          left:          0,
          fontFamily:    'var(--font-mono)',
          fontSize:      8,
          letterSpacing: '0.26em',
          color:         'rgba(242,239,232,0.6)',
          pointerEvents: 'none',
          zIndex:        100003,
          opacity:       0,
          transition:    'opacity 180ms ease',
          whiteSpace:    'nowrap',
          textShadow:    '0 1px 4px rgba(0,0,0,0.8)',
        }}
      />

      {/* ── Radial flash burst at cursor ─────────────────────────────── */}
      <div
        ref={flashRef}
        aria-hidden="true"
        style={{
          display:       'none',
          position:      'fixed',
          width:         76,
          height:        76,
          borderRadius:  '50%',
          zIndex:        99999,
          background:    'radial-gradient(circle, rgba(255,254,248,1) 0%, rgba(255,254,248,0.75) 28%, rgba(255,254,248,0.22) 58%, transparent 75%)',
          boxShadow:     '0 0 22px rgba(255,254,248,0.80), 0 0 6px rgba(255,254,248,0.95)',
          pointerEvents: 'none',
        }}
      />
    </>
  );
}
